// Mock data for templates

import type { Template } from "@/lib/publications/types";

export const mockTemplates: Template[] = [
  {
    id: 1,
    slug: "event-planning-checklist",
    metaTitle: "Event Planning Checklist: Corporate Timeline and Tasks",
    metaDescription:
      "Follow a practical corporate event planning checklist from first brief to final invoice, with timelines, owners, decision gates, and day-of tasks.",
    title: "Ultimate Event Planning Checklist",
    description:
      "Follow a practical corporate event planning checklist from first brief to final invoice, with timelines, owners, decision gates, and day-of tasks.",
    createdAt: "2024-01-15",
    updatedAt: "2026-08-17",
    content: {
      markdown: `Corporate events rarely go sideways because someone forgot to make a to-do list. The trouble starts when approvals, contracts, attendee details, and vendor handoffs live in different places. This 130-item event planning checklist turns those moving parts into one working plan, from the first brief through the final invoice. Each phase ends with a decision gate, so your team knows what is finished, blocked, or ready to move.

Use the **Download Template** button above to get the full checklist as a reusable PDF for planning sessions, handoffs, and on-site checks.

## Table of contents

- [Make the checklist executable before adding tasks](#make-the-checklist-executable-before-adding-tasks)
- [Pick a timeline based on event complexity](#pick-a-timeline-based-on-event-complexity)
- [Corporate event planning checklist by timeline](#corporate-event-planning-checklist-by-timeline)
  - [T-12 to T-6 months: approve the event brief](#t-12-to-t-6-months-approve-the-event-brief)
  - [T-9 to T-4 months: source the destination, venue, and suppliers](#t-9-to-t-4-months-source-the-destination-venue-and-suppliers)
  - [T-6 to T-3 months: build the program and attendee journey](#t-6-to-t-3-months-build-the-program-and-attendee-journey)
  - [T-8 to T-4 weeks: turn the plan into event operations](#t-8-to-t-4-weeks-turn-the-plan-into-event-operations)
  - [T-2 weeks to T-48 hours: freeze, reconcile, and reconfirm](#t-2-weeks-to-t-48-hours-freeze-reconcile-and-reconfirm)
  - [Event day: run one command center](#event-day-checklist-run-one-command-center)
  - [T+1 day to T+30 days: close the event properly](#t1-day-to-t30-days-close-the-event-properly)
- [How to compress the checklist for a short-notice event](#how-to-compress-the-checklist-for-a-short-notice-event)
- [Put the whole plan in one place](#put-the-whole-plan-in-one-place)

## Make the checklist executable before adding tasks

A list of tasks tells you what exists. A working event plan also tells you who owns each task, when it is due, what it depends on, and what “done” looks like.

Set up one master tracker with these fields:

| Field | What to record |
|---|---|
| Task | One concrete action, written with a verb |
| Owner | One person accountable for completion |
| Due date | The date the task must be finished, based on its dependencies |
| Status | Not started, in progress, blocked, ready for approval, or done |
| Dependency | The decision, file, payment, or task that must come first |
| Approver | The person who can make the final call |
| Proof of completion | Link to the signed contract, approved design, manifest, or other final file |
| Budget impact | Estimated, committed, and actual cost, plus the cost center |

Keep the event brief, budget, supplier records, attendee operations, and run of show connected to this tracker. Restrict access to personal attendee information such as accessibility requests, dietary needs, travel details, and emergency contacts.

To reuse it as an event planning checklist template, duplicate the final tracker after closeout and replace the event-specific dates, owners, and requirements.

We built Nowadays for this exact operating problem. Our platform and event specialists bring venue sourcing, proposal comparison, budgets and flight analysis, registration, hotel room blocks, vendor coordination, and attendee operations into one place. The checklist stays useful because the underlying work is connected.

## Pick a timeline based on event complexity

Start with the event date and work backward from the deadlines that carry financial or operational consequences. Venue deposits, room-block cutoffs, catering guarantees, print production, and speaker content deadlines usually matter more than a generic “three months out” date.

These planning ranges are useful starting points:

| Event profile | Working runway | Add more time when… |
|---|---:|---|
| Local executive dinner or workshop, fewer than 50 attendees | 6–10 weeks | Senior calendars, custom production, or procurement reviews are involved |
| One-day corporate meeting, 50–200 attendees | 3–5 months | Attendees will travel, several suppliers are needed, or registration is complex |
| Company offsite, retreat, or sales kickoff | 4–8 months | You need a hotel room block, international travel, activities, or several venues |
| Multi-day conference or summit, 200+ attendees | 9–12+ months | The event includes sponsors, exhibitors, a large production build, or public ticketing |

Shorter timelines can work. They require faster decisions and a simpler event scope, rather than skipping contract, accessibility, safety, or attendee-care work.

## Corporate event planning checklist by timeline

“T-minus” refers to the time before the event begins. Use the ranges as a guide, then replace them with actual dates in your tracker.

### T-12 to T-6 months: approve the event brief

The first planning phase turns an idea into an approved business project. Set the rules before venue photos and activity suggestions take over the conversation.

**Purpose and scope**

- [ ] Name the event owner and executive sponsor.
- [ ] Write one primary objective in a single sentence.
- [ ] Define the intended audience, estimated attendance, format, and location range.
- [ ] Choose a date window and identify conflicts with company, industry, school, religious, and destination calendars.
- [ ] Select two to four success measures and record how each will be captured. Examples include attendance, meetings held, decisions made, satisfaction, pipeline influenced, and budget variance.
- [ ] Draft the high-level attendee experience, from invitation through departure.
- [ ] Record what is out of scope to prevent quiet additions later.

**Governance and money**

- [ ] Set the budget ceiling, cost center, and contingency allowance.
- [ ] Estimate the major cost categories: venue, guest rooms, food and beverage (F&B), audiovisual (AV), travel, transportation, labor, activities, creative, technology, insurance, taxes, service charges, and gratuities.
- [ ] Confirm who can approve scope, budget changes, suppliers, contracts, and attendee exceptions.
- [ ] Map the purchasing process, including supplier onboarding, purchase orders, deposits, payment methods, and lead times.
- [ ] Assign owners for program, venue, production, attendees, travel, communications, finance, and on-site operations.
- [ ] Set the meeting cadence and one channel for decisions and changes.

**Risk and requirements**

- [ ] Identify legal, procurement, brand, information-security, travel-policy, and insurance reviews.
- [ ] Create an initial risk register covering weather, travel disruption, low attendance, supplier failure, medical incidents, security, technology, and budget exposure.
- [ ] Decide which attendee data is genuinely needed, who can access it, and when it will be deleted.
- [ ] Add accessibility and sustainability requirements to the brief before sourcing begins.
- [ ] Record a formal go or no-go date and the person who makes that decision.

**Decision gate:** The brief is approved, the budget has an owner, decision rights are clear, and the go or no-go date is on the calendar.

### T-9 to T-4 months: source the destination, venue, and suppliers

Build the venue request for proposal (RFP) from operational requirements. Every property should receive the same dates, room-night pattern, space needs, agenda assumptions, catering needs, AV scope, accessibility requirements, and commercial terms. The Events Industry Council’s [facility RFP template](https://insights.eventscouncil.org/Portals/0/APEX_Single_Facility_RFP.pdf) captures guest rooms night by night, accessible-room requirements, rate limits, and reservation methods. That level of detail makes proposals far easier to compare.

**Destination and venue sourcing**

- [ ] Map attendee origin cities, likely travel costs, flight schedules, ground-transfer needs, and total travel time.
- [ ] Create venue must-haves and weighted preferences before reviewing options.
- [ ] Specify guest rooms by night, meeting-room layouts, meal functions, breakouts, storage, offices, green rooms, load-in access, and staff space.
- [ ] Send a consistent RFP to a qualified venue shortlist.
- [ ] Compare total program cost, including taxes, service charges, resort or destination fees, internet, power, labor, rentals, and transportation.
- [ ] Score room-block fit, meeting flow, air access, accessibility, production capability, F&B, security, sustainability, and supplier responsiveness.
- [ ] Walk the venue using the attendee route, supplier load-in route, accessible route, and emergency route.
- [ ] Ask for references from recent corporate events with a similar size and program.

We can handle the full [venue sourcing workflow](https://www.nowadays.ai/venue-sourcing), from outreach and follow-ups to normalized proposal comparisons and contract support.

**Contracts and suppliers**

- [ ] Review room rates, the room-block cutoff, and attrition calculation. The cutoff is the last day attendees can usually book at the group rate. Attrition is the contracted room shortfall that can trigger charges.
- [ ] Review the cancellation schedule, deposits, and rebooking terms.
- [ ] Negotiate concessions such as staff rates, complimentary rooms, upgrades, internet, meeting-room rental, parking, storage, and waived fees.
- [ ] Confirm F&B minimums, final-guarantee deadlines, menu pricing, service charges, and what happens when counts change. The final guarantee is the contracted headcount the caterer or venue uses for preparation and billing.
- [ ] Document AV exclusivity, outside-supplier rules, labor minimums, power, rigging, internet, and overtime rates.
- [ ] Clarify billing instructions, authorized signers, incidentals, master-account charges, currency, and invoice timing.
- [ ] Route contracts through the company’s authorized legal, procurement, risk, and finance reviewers.
- [ ] Source the remaining suppliers, which may include production, catering, transportation, activities, photography, security, staffing, accessibility services, and destination support.
- [ ] Add every deposit, cancellation threshold, room-block deadline, and final payment to the master timeline.
- [ ] Create a supplier contact sheet with one primary and one backup contact per company.

**Decision gate:** The venue and essential suppliers are contracted, the committed forecast fits the approved budget, and commercial risks have named owners.

### T-6 to T-3 months: build the program and attendee journey

The agenda, registration form, travel plan, and communications schedule should develop together. A change to one often affects the other three.

**Program and content**

- [ ] Draft the agenda around the event objective and the decisions or behavior it should produce.
- [ ] Include realistic transitions, breaks, meals, travel recovery, and unstructured time.
- [ ] Assign an owner and intended outcome to every session.
- [ ] Invite speakers and send a brief covering audience, objective, format, length, deadlines, AV, and permissions.
- [ ] Set deadlines for titles, descriptions, biographies, headshots, slides, rehearsals, and final files.
- [ ] Plan inclusive participation for presenters and attendees, including stage access, captions, interpretation, assistive listening, and alternative formats where needed.

**Registration and attendee management**

- [ ] Decide who is invited, who approves exceptions, and whether guests or partners are included.
- [ ] Collect only the registration fields needed to run the event.
- [ ] Include attendance dates, name and contact details, dietary needs, accessibility or accommodation requests, travel status, emergency contact where justified, and consent choices where relevant.
- [ ] Publish clear information about how attendee data will be used and who to contact about accommodations.
- [ ] Test registration confirmations, calendar files, waitlists, approval paths, and reminder messages.
- [ ] Set response deadlines early enough to meet room-block, travel, badge, and catering cutoffs.
- [ ] Give one person ownership of incomplete and changed registrations.

Registration should feed the operating plan instead of creating another spreadsheet. Our [Registration](https://www.nowadays.ai/registration) workflow can collect arrival dates and dietary needs, then keep that information connected to attendee logistics.

![Nowadays registration form connected to attendee logistics](/templates/event-registration-workflow.svg)

**Travel, rooms, and communications**

- [ ] Define who books and pays for flights, trains, hotels, transfers, visas, and trip changes.
- [ ] Open the [hotel room block](https://www.nowadays.ai/hotel-room-blocks) and explain booking instructions, covered dates, incidentals, and the cutoff.
- [ ] Build an arrival and departure manifest that can drive transfer waves.
- [ ] Set rules for early arrivals, extended stays, upgrades, companions, and cancellations.
- [ ] Create the communication sequence: save the date, invitation, confirmation, action reminders, know-before-you-go, day-of updates, emergency messages, and follow-up.
- [ ] Publish one attendee hub with the current agenda, travel guidance, venue details, dress code, accessibility contact, and frequently changing information.
- [ ] Give attendees one support channel and response owner.

**For customer-facing, public, or hybrid events**

- [ ] Set ticket prices, refund rules, capacity, waitlist logic, and sales targets where relevant.
- [ ] Build the promotion plan by audience, channel, message, send date, owner, and tracking link.
- [ ] Define sponsor and exhibitor inventory, deadlines, floor-plan needs, lead capture, and reporting.
- [ ] Confirm recording permissions, moderation, remote-speaker checks, captions, time zones, and digital support.
- [ ] Rehearse the stream and keep a backup recording, dial-in path, and attendee message ready.

**Decision gate:** Registration is open and tested, the agenda has accountable owners, travel and room policies are approved, and every attendee message has a send date.

### T-8 to T-4 weeks: turn the plan into event operations

This is where strategy becomes a set of instructions the venue, suppliers, and staff can execute.

**Budget and supplier control**

- [ ] Update every line with estimated, committed, actual, and forecast cost.
- [ ] Add taxes, service charges, gratuities, exchange rates, freight, labor, and overtime where applicable.
- [ ] Match contracted deliverables to supplier production schedules.
- [ ] Review invoices and deposit receipts against the contract.
- [ ] Flag decisions that would trigger cancellation, attrition, rush, or change fees.

**Production and venue plan**

- [ ] Create scaled floor plans for sessions, meals, registration, sponsors, storage, and backstage areas.
- [ ] Confirm capacities, exits, accessible routes, reserved accessible seating, stage access, restrooms, drop-off points, and quiet or wellness space.
- [ ] Review the banquet event orders, the venue’s operating documents for room sets, menus, timing, quantities, and charges.
- [ ] Build the AV plan for sound, screens, lighting, power, internet, recording, captioning, presentations, and backup files.
- [ ] Set speaker-ready and rehearsal times.
- [ ] Create signage and wayfinding for every point where an attendee must make a choice.
- [ ] Plan check-in stations, badge production, on-site changes, walk-ins, and an offline backup list.

Accessibility reaches far beyond a ramp. The Americans with Disabilities Act (ADA) National Network’s [temporary-event accessibility guide](https://adata.org/guide/planning-guide-making-temporary-events-accessible-people-disabilities) covers the full journey, including event information, transportation, parking, routes, participation, communications, restrooms, and staff preparation. Review the site early enough to remove barriers.

**Run of show, staffing, and contingencies**

- [ ] Build a minute-by-minute run of show with time, action, location, owner, cue, and backup action.
- [ ] Create a staff roster with shifts, breaks, role descriptions, reporting lines, and escalation contacts.
- [ ] Choose one day-of staff communication channel and test it.
- [ ] Create a contact sheet for venue, suppliers, speakers, executives, security, medical support, and transportation.
- [ ] Write a contingency matrix with the risk, trigger, decision maker, fallback, attendee message, and cost impact.
- [ ] Plan responses for severe weather, travel disruption, speaker cancellation, illness, low turnout, power or internet failure, and transport delays.
- [ ] Confirm permits, licenses, certificates of insurance, security, medical support, and emergency procedures.
- [ ] Document who can pause the program, evacuate, shelter in place, or approve an emergency message.
- [ ] For public or high-profile gatherings, use the Cybersecurity and Infrastructure Security Agency’s (CISA) [mass gathering tool](https://www.cisa.gov/resources-tools/resources/mass-gathering-security-planning-tool) to structure conversations about the venue, crowd, emergency plans, and physical security.

**Food, materials, and sustainability**

- [ ] Reconcile dietary needs with the caterer and agree how allergens and special meals will be identified and handed out.
- [ ] Include meals and water for speakers, drivers, crew, staff, and volunteers.
- [ ] Confirm the final catering guarantee date and late-addition process.
- [ ] Order badges, signage, supplies, gifts, and rentals with time for proofing and shipping delays.
- [ ] Decide how materials will be stored, delivered, inventoried, reused, donated, recycled, or removed.
- [ ] Set practical goals for transport, food waste, reusable materials, water, and supplier reporting. The [Sustainable Event Standards](https://insights.eventscouncil.org/Sustainability/Sustainability-Events-Standards) organize this work across destinations, venues, accommodation, production, F&B, and suppliers.

**Decision gate:** The run of show, floor plans, staffing, supplier schedules, attendee plan, and contingencies have owners. No high-impact task is unassigned.

### T-2 weeks to T-48 hours: freeze, reconcile, and reconfirm

Late changes are sometimes necessary. Uncontrolled changes are expensive. Set a change cutoff, name the person who can approve exceptions, and log every accepted change in the budget and operating documents.

- [ ] Close registration or activate the late-registration process.
- [ ] Reconcile registration, the rooming list, travel bookings, transfer manifest, meal counts, badges, and accessibility requests.
- [ ] Send final guarantees and dietary requirements by the contracted deadlines.
- [ ] Review final banquet event orders, floor plans, menus, room sets, signage locations, and charges.
- [ ] Hold an all-supplier production call covering access, load-in, parking, credentials, contacts, timing, handoffs, power, internet, storage, and breakdown.
- [ ] Issue the final run of show with a version number and distribution list.
- [ ] Collect final speaker files in cloud and offline formats.
- [ ] Rehearse key transitions, walk-ons, videos, demos, awards, executive remarks, and emergency announcements.
- [ ] Send the know-before-you-go message with agenda, travel, venue, dress, weather, accessibility, and support details.
- [ ] Brief staff on attendee care, accommodations, safety, communications, issue logging, and escalation.
- [ ] Test the staff channel, check-in equipment, badge printer, AV, internet, captions, presentation playback, and backups.
- [ ] Confirm payments, purchasing cards, gratuities, petty cash, and authorized on-site spend.
- [ ] Pack an operations kit with adapters, chargers, batteries, tape, scissors, cable ties, markers, labels, first-aid supplies, stain remover, basic sewing supplies, and spare badges.
- [ ] Walk the venue one final time from the attendee, supplier, accessibility, and emergency perspectives.
- [ ] Hold a green-light meeting and record every remaining open item, owner, and deadline.

**Decision gate:** Counts reconcile, suppliers have acknowledged the final documents, staff know their roles, critical systems have been tested, and open items have timed owners.

### Event day checklist: run one command center

The event owner needs a single view of the day. Keep the latest run of show, contact sheet, floor plans, attendee list, manifests, contingency plan, and issue log available online and offline.

- [ ] Open the command center before supplier load-in.
- [ ] Confirm venue access, room sets, signage, accessible routes, exits, and back-of-house spaces.
- [ ] Check each supplier against the contracted scope and production schedule.
- [ ] Test microphones, playback, screens, lighting, Wi-Fi, captioning, check-in, printers, and backup files.
- [ ] Brief speakers and executives on timing, stage movement, cues, and contacts.
- [ ] Open check-in with a plan for missing names, replacements, walk-ins, and accommodation requests.
- [ ] Verify meal timing, allergen controls, special-meal handoff, water, and staff meals.
- [ ] Assign one person to transportation dispatch and attendee travel changes.
- [ ] Monitor timing, room capacity, temperature, noise, queues, and attendee support needs.
- [ ] Log incidents, approved changes, extra costs, supplier misses, and useful observations as they happen.
- [ ] Capture attendance and agreed success data without collecting more personal information than needed.
- [ ] Give staff scheduled breaks and explicit handoffs.
- [ ] Send attendee updates from one approved source.
- [ ] Complete teardown, rental returns, inventory, lost and found, damage review, and venue sign-off.

### T+1 day to T+30 days: close the event properly

An event is still open while money, data, assets, and follow-ups are unresolved.

- [ ] Send thank-you messages, promised materials, and a short feedback survey.
- [ ] Resolve lost and found, rental returns, shipment tracking, and stored materials.
- [ ] Collect invoices and reconcile them against contracts, banquet event orders, approved changes, deposits, purchasing cards, and receipts.
- [ ] Check room pickup, meaning the rooms actually booked or used, along with attrition exposure, unused deposits, credits, duplicate charges, and final supplier balances.
- [ ] Update the budget with actuals and explain material variances.
- [ ] Calculate the success measures defined in the original brief.
- [ ] Hold a team debrief while details are fresh. Review the issue log, attendee feedback, supplier performance, and missed handoffs.
- [ ] Score suppliers on responsiveness, accuracy, quality, billing, attendee care, and recovery when plans changed.
- [ ] Produce a short post-event report for stakeholders with outcomes, spend, attendance, room-block performance, feedback, incidents, accessibility delivery, sustainability results, and next actions.
- [ ] Archive the approved brief, contracts, final operating documents, invoices, report, and lessons learned in a reusable event record.
- [ ] Delete or retain attendee data according to the plan set at the start.
- [ ] Assign each follow-up action an owner and due date.
- [ ] Place a tentative hold for the next event if the program will repeat.

Treat the post-event report as the starting point for the next brief. Actual room pickup, F&B spend, attendance, supplier performance, and production notes produce stronger forecasts and more precise RFPs than memory does.

**Decision gate:** The final budget is approved, suppliers are closed out, attendee data is handled as planned, results are reported, and follow-up work has left the event project.

## How to compress the checklist for a short-notice event

When the timeline shrinks, preserve the order of decisions and reduce the number of custom elements.

1. **Lock the brief in one meeting.** Leave with an objective, budget, date, audience, owner, approver, and go or no-go call.
2. **Simplify the attendee journey.** Favor one venue, fewer transfers, a shorter agenda, standard menus, and existing event formats.
3. **Source from a tight brief.** Send the same requirements to a small qualified venue set and establish a same-day decision window.
4. **Run workstreams in parallel.** Venue, program, registration, travel, production, and communications can move together once their decision rights are clear.
5. **Bring risk deadlines forward.** Contract review, insurance, accessibility, security, deposits, room blocks, and catering guarantees cannot wait for the rest of the creative work.
6. **Set an early change cutoff.** After that point, only the named event owner can approve a change and its cost.
7. **Keep the operating documents.** A short timeline still needs a final run of show, contact sheet, floor plan, attendee manifest, budget, and contingency matrix.

## Put the whole plan in one place

The best checklist creates fewer follow-ups, cleaner handoffs, and a calmer event day. We pair AI automation with expert event specialists to handle the work behind the boxes, from venue outreach and proposal comparison through registration, travel, room blocks, vendors, and on-site execution.

[Try our platform](https://www.nowadays.ai/create) to plan your next corporate event in one place.`,
    },
    category: "Planning & Organization",
    featured: true,
    templateType: "checklist",
    previewImage:
      "https://media.cnn.com/api/v1/images/stellar/prod/200619162441-list-making-stock.jpg?q=x_3,y_156,h_1684,w_2993,c_crop/h_833,w_1480",
    downloadUrl: "/templates/Event-Planning-Checklist.pdf",
    fileFormat: ".pdf",
    fileSize: "52 KB",
    useCases: ["Corporate Retreats", "Team Building", "Conferences", "Executive Meetings"],
    tags: ["planning", "checklist", "organization", "events"],
    downloadCount: 2847,
    difficulty: "beginner",
  },
  {
    id: 2,
    slug: "team-dinner-planning-guide",
    metaTitle: "Team Dinner Planning Guide | Checklist & Tips",
    metaDescription:
      "Plan the perfect team dinner with this comprehensive guide. Includes checklist, venue selection tips, and menu planning advice.",
    title: "Team Dinner Planning Guide",
    description:
      "A step-by-step guide to organizing successful team dinners, from choosing the right venue to managing RSVPs and dietary needs.",
    createdAt: "2024-02-01",
    updatedAt: "2024-02-01",
    content: {
      markdown: `# Team Dinner Planning Guide

Ensure your next team dinner is a success with this detailed planning guide.

## Step 1: Define Objectives
-  Clarify the purpose (celebration, team building, milestone, etc.)
-  Set a budget

## Step 2: Choose Date & Venue
-  Poll team for availability
-  Research and shortlist venues
-  Confirm reservation

## Step 3: Plan the Menu
-  Collect dietary restrictions
-  Select menu options
-  Arrange for special requests

## Step 4: Send Invitations
-  Create and send invites
-  Track RSVPs

## Step 5: Finalize Details
-  Confirm headcount with venue
-  Arrange transportation if needed
-  Prepare any team activities or speeches

## Step 6: Event Day
-  Arrive early for setup
-  Welcome guests
-  Enjoy the dinner!

## Post-Event
-  Send thank you notes
-  Gather feedback for future events

Use this guide to streamline your planning and create memorable team experiences.`,
    },
    category: "Planning & Organization",
    featured: false,
    templateType: "checklist",
    previewImage: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop",
    downloadUrl: "/templates/Team-Dinner-Planning-Guide.pdf",
    fileFormat: ".pdf",
    fileSize: "180 KB",
    useCases: ["Team Building", "Corporate Events", "Social Gatherings"],
    tags: ["planning", "dinner", "checklist", "team"],
    downloadCount: 1123,
    difficulty: "beginner",
  },
  {
    id: 3,
    slug: "site-visit-feedback",
    metaTitle: "Site Visit Feedback Template | Venue Evaluation",
    metaDescription:
      "Collect structured feedback after site visits with this easy-to-use template. Includes scoring and comments sections.",
    title: "Site Visit Feedback Template",
    description:
      "A spreadsheet template for capturing feedback and evaluations during venue site visits. Includes scoring, comments, and recommendations.",
    createdAt: "2024-01-28",
    updatedAt: "2024-01-28",
    content: {
      markdown: `# Site Visit Feedback Template

Use this template to record your impressions and evaluations during venue site visits.

## Sections Included

- Venue name and address
- Date of visit
- Contact person
- Accessibility and location
- Cleanliness and ambiance
- Facilities and amenities
- Staff professionalism
- Catering options
- AV/Tech capabilities
- Overall impression
- Scoring (1-5)
- Additional comments

## How to Use

1. Download and open the CSV in your preferred spreadsheet tool.
2. Fill out each section during or after your site visit.
3. Use the scoring system to compare venues objectively.
4. Add comments and recommendations for your team.

This template helps you make informed decisions when selecting event venues.`,
    },
    category: "Evaluation & Analytics",
    featured: true,
    templateType: "spreadsheet",
    previewImage:
      "https://i0.wp.com/theluxurytravelexpert.com/wp-content/uploads/2021/03/PALACE-HOTEL-A-LUXURY-COLLECTION-HOTEL.jpg?ssl=1",
    downloadUrl: "/templates/Site-Visit-Feedback.xlsx",
    fileFormat: ".xlsx",
    fileSize: "45 KB",
    useCases: ["Venue Selection", "Event Planning", "Quality Control"],
    tags: ["feedback", "site visit", "evaluation", "venues"],
    downloadCount: 857,
    difficulty: "beginner",
  },
  {
    id: 4,
    slug: "packing-list",
    metaTitle: "Event Packing List Template | Travel Checklist",
    metaDescription:
      "Never forget an essential item again. Use this packing list template for retreats, conferences, and team travel.",
    title: "Event Packing List",
    description:
      "A comprehensive packing checklist for event attendees and organizers. Covers essentials for business, leisure, and team retreats.",
    createdAt: "2024-01-20",
    updatedAt: "2024-01-20",
    content: {
      markdown: `# Event Packing List

Stay organized and prepared for your next event or retreat.

## Essentials
-  Event tickets/confirmation
-  Photo ID/passport
-  Travel itinerary
-  Accommodation details

## Clothing
-  Business attire
-  Casual wear
-  Workout clothes
-  Weather-appropriate gear

## Technology
-  Laptop/tablet
-  Chargers and adapters
-  Presentation materials
-  Headphones

## Personal Items
-  Toiletries
-  Medications
-  Snacks and water bottle

## Event-Specific
-  Name badge/lanyard
-  Marketing materials
-  Gifts or giveaways

Customize this list for your specific event needs!`,
    },
    category: "Planning & Organization",
    featured: false,
    templateType: "checklist",
    previewImage:
      "https://images.pexels.com/photos/7368218/pexels-photo-7368218.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop",
    downloadUrl: "/templates/Packing-List.pdf",
    fileFormat: ".pdf",
    fileSize: "95 KB",
    useCases: ["Retreats", "Conferences", "Team Travel"],
    tags: ["packing", "checklist", "travel", "events"],
    downloadCount: 1342,
    difficulty: "beginner",
  },
  {
    id: 5,
    slug: "flight-analysis",
    metaTitle: "Flight Analysis Spreadsheet | Travel Cost Comparison",
    metaDescription: "Analyze and compare flight options for your team with this detailed spreadsheet template.",
    title: "Flight Analysis Spreadsheet",
    description:
      "A spreadsheet for comparing flight options, costs, and schedules for group or individual travel. Includes cost breakdowns and scoring.",
    createdAt: "2024-01-18",
    updatedAt: "2024-01-18",
    content: {
      markdown: `# Flight Analysis Spreadsheet

Compare flight options and optimize your travel planning.

## Features

- Airline, flight number, and route details
- Departure and arrival times
- Total travel duration
- Layover information
- Ticket class and amenities
- Cost per ticket and total cost
- Baggage policies
- Scoring system for convenience and value

## How to Use

1. Enter your travel requirements and preferences.
2. List available flight options.
3. Fill in details for each flight.
4. Use the scoring system to identify the best option.

Ideal for group travel, corporate events, and retreat planning.`,
    },
    category: "Budget & Finance",
    featured: false,
    templateType: "spreadsheet",
    previewImage: "https://www.rd.com/wp-content/uploads/2020/01/GettyImages-1131335393-e1650030686687.jpg",
    downloadUrl: "/templates/Flight-Analysis.xlsx",
    fileFormat: ".xlsx",
    fileSize: "320 KB",
    useCases: ["Travel Planning", "Budgeting", "Logistics"],
    tags: ["flights", "analysis", "spreadsheet", "travel"],
    downloadCount: 678,
    difficulty: "intermediate",
  },
  {
    id: 6,
    slug: "attendee-management",
    metaTitle: "Attendee Management Spreadsheet | Registration Tracker",
    metaDescription: "Track attendee information and registration status with this easy-to-use spreadsheet template.",
    title: "Attendee Management Spreadsheet",
    description:
      "A CSV template for managing attendee lists, contact details, registration status, and special requirements.",
    createdAt: "2024-01-15",
    updatedAt: "2024-01-15",
    content: {
      markdown: `# Attendee Management Spreadsheet

Keep your attendee information organized and accessible.

## Columns Included

- Full name
- Email address
- Company/Organization
- Registration status
- Dietary restrictions
- Accessibility needs
- Payment status
- Notes

## How to Use

1. Download and open the CSV file.
2. Enter attendee details as registrations come in.
3. Track status and special requirements for each attendee.

Perfect for conferences, retreats, and corporate events.`,
    },
    category: "Registration & Forms",
    featured: false,
    templateType: "spreadsheet",
    previewImage:
      "https://goeshow.com/wp-content/uploads/2024/05/The-Power-of-Customizable-Reporting-in-Abstract-Submissions-scaled.jpg",
    downloadUrl: "/templates/Attendee-Management.xlsx",
    fileFormat: ".xlsx",
    fileSize: "60 KB",
    useCases: ["Attendee Tracking", "Registration", "Data Collection"],
    tags: ["attendees", "management", "spreadsheet", "registration"],
    downloadCount: 1024,
    difficulty: "beginner",
  },
  {
    id: 7,
    slug: "4-day-offsite-itinerary-two-options",
    metaTitle: "4-Day Offsite Itinerary (Two Options) | Retreat Planning",
    metaDescription:
      "Choose from two sample 4-day offsite itineraries. Includes schedules, activities, and team-building ideas.",
    title: "4-Day Offsite Itinerary (Two Options)",
    description:
      "Two sample itineraries for a 4-day company offsite, including daily schedules, activities, and team-building sessions.",
    createdAt: "2024-01-10",
    updatedAt: "2024-01-10",
    content: {
      markdown: `# 4-Day Offsite Itinerary (Two Options)

Plan your next offsite with these ready-to-use sample itineraries.

## Option 1: Classic Retreat

**Day 1:** Arrival, welcome dinner, icebreakers  
**Day 2:** Morning workshops, afternoon team-building, evening social  
**Day 3:** Strategy sessions, outdoor activities, group dinner  
**Day 4:** Wrap-up, feedback session, departures

## Option 2: Adventure Focus

**Day 1:** Arrival, orientation, local exploration  
**Day 2:** Adventure activity (hiking, kayaking), team lunch, fireside chat  
**Day 3:** Creative workshops, free time, themed dinner  
**Day 4:** Closing session, awards, departures

Each itinerary includes built-in time for networking, relaxation, and reflection. Customize to fit your team's goals!`,
    },
    category: "Planning & Organization",
    featured: false,
    templateType: "document",
    previewImage:
      "https://imageio.forbes.com/specials-images/imageserve/648f06a6152abcf5ef5e44a9/e125175d2a6f8fed0c71b983c4d1368d/960x0.jpg?format=jpg&width=960",
    downloadUrl: "/templates/4-Day-Offsite-Itinerary-Two-Options.pdf",
    fileFormat: ".pdf",
    fileSize: "210 KB",
    useCases: ["Offsite Planning", "Team Retreats", "Event Scheduling"],
    tags: ["itinerary", "offsite", "planning", "retreat"],
    downloadCount: 789,
    difficulty: "beginner",
  },
  {
    id: 8,
    slug: "1-week-tropical-company-retreat-itinerary",
    metaTitle: "1-Week Tropical Company Retreat Itinerary | Sample Schedule",
    metaDescription:
      "A detailed 1-week itinerary for a tropical company retreat. Includes daily activities, excursions, and team sessions.",
    title: "1-Week Tropical Company Retreat Itinerary",
    description:
      "A sample schedule for a week-long tropical company retreat, featuring daily agendas, excursions, and team-building activities.",
    createdAt: "2024-01-05",
    updatedAt: "2024-01-05",
    content: {
      markdown: `# 1-Week Tropical Company Retreat Itinerary

Make the most of your company retreat with this detailed 7-day itinerary.

## Day 1: Arrival & Welcome
- Check-in, welcome drinks, orientation

## Day 2: Team Workshops
- Morning strategy session, afternoon breakout groups, evening networking

## Day 3: Excursion Day
- Guided tour or adventure activity, group lunch, free evening

## Day 4: Professional Development
- Guest speaker, skill-building workshops, team dinner

## Day 5: Community Service
- Volunteer project, reflection session, beach BBQ

## Day 6: Recreation & Relaxation
- Yoga, spa, water sports, sunset cruise

## Day 7: Closing & Departures
- Feedback session, closing remarks, departures

Customize this itinerary to fit your team's goals and preferences!`,
    },
    category: "Planning & Organization",
    featured: true,
    templateType: "document",
    previewImage:
      "https://q-xx.bstatic.com/xdata/images/hotel/max500/92797523.jpg?k=1d2827e4f5db52878d3d80d62b75966dd6ff19b104c34d1ee1c7fbf456171c62&o=",
    downloadUrl: "/templates/1-Week-Tropical-Company-Retreat-Itinerary.pdf",
    fileFormat: ".pdf",
    fileSize: "250 KB",
    useCases: ["Retreat Planning", "Team Building", "Executive Events"],
    tags: ["itinerary", "retreat", "planning", "tropical"],
    downloadCount: 543,
    difficulty: "beginner",
  },
];

// Template categories for filtering
export const templateCategories = [
  "All Templates",
  "Planning & Organization",
  "Registration & Forms",
  "Communications",
  "Budget & Finance",
  "Vendor Management",
  "Evaluation & Analytics",
];

// Template types for filtering
export const templateTypes = ["All Types", "checklist", "spreadsheet", "form", "email", "document", "presentation"];
